import { Address, NewAddress } from 'hooks/usePayment'
import { useContext, useState } from 'react'
import { PaymentContext } from 'contexts/PaymentContext'
import {
  Box,
  Button,
  Dialog,
  Divider,
  Grid,
  Paper,
  Typography,
  Chip,
  IconButton,
  CircularProgress
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import PersonIcon from '@mui/icons-material/Person'
import PhoneIcon from '@mui/icons-material/Phone'
import BusinessIcon from '@mui/icons-material/Business'
import StarIcon from '@mui/icons-material/Star'
import ConfirmDeleteDialog from 'components/ConfirmDeleteDialog'
import AddressForm from 'pages/MyAccount/AddressForm'
import { LoadingContext } from 'contexts/LoadingContext'
import { useTranslation } from 'react-i18next'

const defaultAddress: Address = {
  id: '',
  customerName: '',
  companyName: '',
  phoneNumber: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  zipCode: '',
  isPrimaryAddress: false
}

const AddressBook = () => {
  const { t } = useTranslation()
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [addressToDelete, setAddressToDelete] = useState<string | null>(null)
  const [isAddressFormOpen, setIsAddressFormOpen] = useState(false)
  const [selectedAddress, setSelectedAddress] =
    useState<Address>(defaultAddress)
  const {
    addresses,
    updateAddress,
    deleteAddress,
    addAddress,
    isLoadingAddresses
  } = useContext(PaymentContext)
  const { showLoading, stopLoading } = useContext(LoadingContext)
  const [mode, setMode] = useState<'add' | 'edit'>('add')

  const handleEditAddress = (address: Address) => {
    setSelectedAddress(address)
    setMode('edit')
    setIsAddressFormOpen(true)
  }

  const handleAddAddress = () => {
    setSelectedAddress(defaultAddress)
    setMode('add')
    setIsAddressFormOpen(true)
  }

  const handleFormClose = () => {
    setIsAddressFormOpen(false)
  }

  const handleFormSubmit = async (addressDetails: Address) => {
    try {
      if (mode === 'edit' && selectedAddress) {
        showLoading()
        await updateAddress(addressDetails)
        stopLoading()
      } else {
        const newAddress: NewAddress = {
          customerName: addressDetails.customerName,
          companyName: addressDetails.companyName,
          phoneNumber: addressDetails.phoneNumber,
          addressLine1: addressDetails.addressLine1,
          addressLine2: addressDetails.addressLine2,
          city: addressDetails.city,
          state: addressDetails.state,
          zipCode: addressDetails.zipCode
        }
        showLoading()
        await addAddress(newAddress)
        stopLoading()
      }
      setIsAddressFormOpen(false)
    } catch (error) {
      console.error('Error submitting address details:', error)
    }
  }
  const handleDeleteClick = (id: string) => {
    setAddressToDelete(id)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (addressToDelete !== null) {
      setDeleteDialogOpen(false)
      showLoading()
      await deleteAddress(addressToDelete)
      setAddressToDelete(null)
      stopLoading()
    }
  }

  return (
    <Box sx={{ mt: 2, mb: 6 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4
        }}
      >
        <Button
          variant='contained'
          startIcon={<AddIcon />}
          onClick={handleAddAddress}
          sx={{
            borderRadius: '6px',
            px: 2,
            py: 1,
            fontWeight: 400,
            textTransform: 'none'
          }}
        >
          {t('myAccount.addressBook.addAddress')}
        </Button>
      </Box>

      {isLoadingAddresses ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px'
          }}
        >
          <CircularProgress />
        </Box>
      ) : addresses && addresses.length > 0 ? (
        <Grid container spacing={3}>
          {addresses.map(address => (
            <Grid item xs={12} md={6} key={address.id}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  height: '100%',
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': {
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                    transform: 'translateY(-4px)'
                  }
                }}
              >
                {address.isPrimaryAddress && (
                  <Chip
                    icon={<StarIcon sx={{ fontSize: 16 }} />}
                    label={t('myAccount.addressBook.primary')}
                    color='primary'
                    size='small'
                    sx={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      fontWeight: 600,
                      fontSize: '0.75rem'
                    }}
                  />
                )}

                <Box sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 2
                    }}
                  >
                    <PersonIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                    <Typography variant='h6' sx={{ fontWeight: 700 }}>
                      {address.customerName}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                    <LocationOnIcon
                      sx={{ color: 'text.secondary', fontSize: 18, mt: 0.3 }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant='body2'
                        color='text.secondary'
                        sx={{ lineHeight: 1.6 }}
                      >
                        {address.addressLine1}
                        {address.addressLine2 && (
                          <>
                            <br />
                            {address.addressLine2}
                          </>
                        )}
                        <br />
                        {address.city}, {address.state} {address.zipCode}
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1
                    }}
                  >
                    <PhoneIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                    <Typography variant='body2' color='text.secondary'>
                      {address.phoneNumber}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BusinessIcon
                      sx={{ color: 'text.secondary', fontSize: 18 }}
                    />
                    <Typography variant='body2' color='text.secondary'>
                      {address.companyName}
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box
                  sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}
                >
                  <Button
                    variant='outlined'
                    size='small'
                    startIcon={<EditIcon />}
                    onClick={() => handleEditAddress(address)}
                    sx={{
                      borderRadius: '6px',
                      px: 2,
                      textTransform: 'none',
                      fontWeight: 400
                    }}
                  >
                    {t('myAccount.addressBook.edit')}
                  </Button>
                  <IconButton
                    size='small'
                    onClick={() => handleDeleteClick(address.id)}
                    sx={{
                      color: 'primary.main'
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Paper
          sx={{
            p: 6,
            textAlign: 'center',
            bgcolor: 'grey.50',
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 3
          }}
        >
          <LocationOnIcon
            sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }}
          />
          <Typography variant='h6' color='text.secondary' gutterBottom>
            {t('myAccount.addressBook.noAddresses')}
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
            {t('myAccount.addressBook.addFirstAddress')}
          </Typography>
          <Button
            variant='contained'
            startIcon={<AddIcon />}
            onClick={handleAddAddress}
            sx={{
              borderRadius: 2,
              px: 3,
              textTransform: 'none',
              fontWeight: 400
            }}
          >
            {t('myAccount.addressBook.addYourFirstAddress')}
          </Button>
        </Paper>
      )}

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title={t('confirmDelete.deleteAddress')}
        message={t('confirmDelete.confirmMessage')}
      />

      <Dialog
        open={isAddressFormOpen}
        onClose={handleFormClose}
        fullWidth
        maxWidth='sm'
      >
        <AddressForm
          initialValues={selectedAddress}
          onSubmit={handleFormSubmit}
          onCancel={handleFormClose}
          mode={mode}
        />
      </Dialog>
    </Box>
  )
}

export default AddressBook
